import { Typography, FormControl, Button, Popover, Box, useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ProductFormProductLine } from "../../../ProductForm.types";
import { ProductLineFormTreeSelector } from "src/components/Selector/ProductLineSelector/ProductLineFormTreeSelector";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useGetAllProductLines } from "src/api/client-api/productLines/useProductLines";

type GammeFieldProps = {
    productLineIRI: IRI | null;
    setGamme: (newGamme: ProductFormProductLine | null) => void;
};

export const GammeField = (props: GammeFieldProps) => {
    const { productLineIRI, setGamme } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedProductLineName, setSelectedProductLineName] = useState<ProductFormProductLine | undefined>(
        undefined,
    );

    // Get all product lines to find the current selection and for TreeView
    const { productLines = [], searchApi } = useGetAllProductLines();

    // Initialize the selected product line name from the IRI
    useEffect(() => {
        if (productLineIRI && productLines.length > 0) {
            const currentProductLine = productLines.find((pl) => pl["@id"] === productLineIRI);
            if (currentProductLine) {
                setSelectedProductLineName(currentProductLine);
            }
        } else if (!productLineIRI) {
            setSelectedProductLineName(undefined);
        }
    }, [productLineIRI, productLines]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelectionChange = (productLineId: string | undefined) => {
        if (productLineId === undefined) {
            setGamme(null);
            handleClose();
            return;
        }
        // Find the complete product line data
        const selectedProductLine = productLines.find((pl) => pl["@id"] === productLineId);
        if (selectedProductLine) {
            const productLineData: ProductFormProductLine = {
                "@id": selectedProductLine["@id"],
                name: selectedProductLine.name,
                parent: selectedProductLine.parent,
            };
            setGamme(productLineData);
            setSelectedProductLineName(selectedProductLine);
        }
        // handleClose();
    };

    const open = Boolean(anchorEl);

    const label =
        selectedProductLineName ?
            selectedProductLineName.parent ?
                `${selectedProductLineName.parent.name} > ${selectedProductLineName.name}`
            :   `${selectedProductLineName.name}`
        :   t("component.select.placeholder");

    return (
        <FormControl size="small">
            <Typography variant="body-semibold" sx={{ paddingBottom: theme.spacing(1) }}>
                {`${t("gamme")}*`}
            </Typography>

            <Box sx={{ width: "100%" }}>
                <Button
                    variant="outlined"
                    onClick={handleClick}
                    endIcon={<ExpandMore />}
                    sx={{
                        width: "100%",
                        justifyContent: "space-between",
                        textAlign: "left",
                        color: selectedProductLineName ? theme.palette.text.primary : theme.palette.text.secondary,
                        borderColor: theme.custom.grey[500],
                        "&:hover": {
                            borderColor: theme.palette.primary.main,
                        },
                    }}
                >
                    {label}
                </Button>

                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    disableRestoreFocus={true}
                    disableEnforceFocus={true}
                >
                    <ProductLineFormTreeSelector
                        selectedProductLine={selectedProductLineName}
                        onSelectionChange={handleSelectionChange}
                        noSelectedItemPlaceholder={t("component.select.placeholder")}
                    />
                </Popover>
            </Box>
        </FormControl>
    );
};
